import React from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function PrivacyPolicyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="space-y-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
            <p className="text-muted-foreground">Last updated: May 2, 2026</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>1. Information We Collect</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <div>
                <h3 className="font-bold text-foreground mb-2">Account Information:</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Name, email address, phone number</li>
                  <li>Business name and description (for sellers)</li>
                  <li>Location and address information</li>
                  <li>Profile photos and verification documents</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-foreground mb-2">Listing Data:</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Product/service descriptions and images</li>
                  <li>Pricing and availability information</li>
                  <li>Location data for listings</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-foreground mb-2">Usage Data:</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Browser type, device information, IP address</li>
                  <li>Pages visited, time spent on platform</li>
                  <li>Search queries and filters used</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>2. How We Use Your Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>We use collected information to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Provide and maintain our marketplace platform</li>
                <li>Verify seller identities and prevent fraud</li>
                <li>Process subscription payments</li>
                <li>Send notifications about inquiries and listings</li>
                <li>Improve platform functionality and user experience</li>
                <li>Comply with legal obligations</li>
                <li>Enforce our Terms of Service</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>3. Information Sharing</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p><strong className="text-foreground">We DO NOT sell your personal information.</strong></p>
              <p>We may share information in these limited circumstances:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong className="text-foreground">With Other Users:</strong> When you post a listing, your business name, location, and contact info become visible to potential buyers</li>
                <li><strong className="text-foreground">With Service Providers:</strong> Payment processors, SMS/email providers for platform operations</li>
                <li><strong className="text-foreground">For Legal Compliance:</strong> When required by law, court order, or government request</li>
                <li><strong className="text-foreground">To Prevent Harm:</strong> When necessary to investigate fraud, security issues, or violations of our Terms</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>4. Contact Information Disclosure</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>
                FarmPack AI follows a <strong className="text-foreground">controlled disclosure</strong> policy for contact information:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Seller phone numbers are shown ONLY after a buyer submits an inquiry</li>
                <li>Email addresses are never publicly displayed</li>
                <li>Buyers' contact info is shared with sellers only after an inquiry is made</li>
                <li>We may implement phone number masking in the future for additional privacy</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>5. Data Storage & Security</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>
                Your data is stored on Cloudflare's secure infrastructure (D1 database and Durable Objects). 
                We implement industry-standard security measures including:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Encryption of data in transit (TLS/SSL)</li>
                <li>Access controls and authentication</li>
                <li>Regular security audits</li>
                <li>Encrypted storage of verification documents</li>
              </ul>
              <p>
                <strong className="text-foreground">Note:</strong> While we strive to protect your information, 
                no method of transmission over the internet is 100% secure.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>6. Data Retention</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>We retain your data as follows:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong className="text-foreground">Account Data:</strong> Until you request deletion</li>
                <li><strong className="text-foreground">Listing Data:</strong> Until removed by you or flagged for violations</li>
                <li><strong className="text-foreground">Inquiry Messages:</strong> 90 days after closing (for dispute resolution)</li>
                <li><strong className="text-foreground">Verification Documents:</strong> 2 years after account closure</li>
                <li><strong className="text-foreground">Usage Logs:</strong> 12 months (anonymized after 90 days)</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>7. Your Rights (GDPR/NDPR Compliance)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>Depending on your location, you may have the following rights:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong className="text-foreground">Access:</strong> Request a copy of your personal data</li>
                <li><strong className="text-foreground">Rectification:</strong> Correct inaccurate or incomplete data</li>
                <li><strong className="text-foreground">Erasure:</strong> Request deletion of your personal data</li>
                <li><strong className="text-foreground">Portability:</strong> Receive your data in a structured, machine-readable format</li>
                <li><strong className="text-foreground">Objection:</strong> Object to certain types of processing</li>
              </ul>
              <p>
                To exercise these rights, contact us at privacy@farmpack-ai.com. We will respond within 30 days.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>8. Children's Privacy</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>
                FarmPack AI is not intended for children under 18. We do not knowingly collect personal 
                information from children. If you believe we have collected data from a child, please contact 
                us immediately for deletion.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>9. Third-Party Links</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>
                Our platform may contain links to third-party websites (e.g., seller websites). We are not 
                responsible for the privacy practices or content of these external sites. Please review their 
                privacy policies separately.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>10. Updates to This Policy</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>
                We may update this Privacy Policy from time to time. We will notify users of significant 
                changes via email or platform notifications. Your continued use of FarmPack AI after changes 
                constitutes acceptance of the updated policy.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>11. Contact Us</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>For privacy-related inquiries:</p>
              <ul className="list-none space-y-1">
                <li><strong className="text-foreground">Email:</strong> privacy@farmpack-ai.com</li>
                <li><strong className="text-foreground">Address:</strong> FarmPack AI, Data Protection Officer, Accra, Ghana</li>
                <li><strong className="text-foreground">Phone:</strong> +233 XX XXX XXXX</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
